import React, {
  useEffect,
  useState,
  useRef,
  ClassAttributes,
  HTMLAttributes,
} from 'react';
import styled, { useTheme } from 'styled-components';
import LinkComponent from '../LinkComponent';
import breakpoints from '../breakpoints';
import ChevronRight from '../svgs/chevron-right.svg';

interface NavDesktopProps {
  navLinks: any[];
  megaMenuActive: number;
  setKeyboardNavActive: (active: boolean) => void;
  setMegaMenuActive: (index: number) => void;
  keyboardNavActive: boolean;
  getInternalLink: (link: any) => string;
}

const Wrapper = styled.nav`
  display: none;
  height: 90px;

  @media (min-width: ${breakpoints.lg}) {
    display: flex;
    justify-content: center;
    flex: 1;
  }
`;

const NavDesktopMenuItem = styled.div`
  display: flex;
  cursor: pointer;
  align-items: center;
  font-size: ${({ theme }) => theme.navigation.NavDesktopMenuItemFontSize};
  font-weight: inherit;
  margin-bottom: -1rem;
  padding: 0.5rem 0.625rem;
  margin-top: -1rem;
  text-transform: ${({ theme }) => theme.navigation.mainItemTextTransform};
  &:first-child {
    padding-left: 5rem;
    margin-left: -5rem;
  }
  svg {
    transition: all 0.5s ease;
    fill: ${({ theme }) => theme.colors.defaultFont};
  }
  a {
    transition: all 0.5s ease;
    text-decoration: none;
    &:hover {
      text-decoration: underline;

      svg {
        fill: ${({ theme }) => theme.colors.defaultFont};
      }
    }
  }
`;

const NavDesktopLink = styled(LinkComponent)`
  display: flex;
  align-self: stretch;
  align-items: center;
  color: inherit;
  font-weight: ${({ theme }) => theme.navigation.fontWeight};
  padding: 0.25rem;
  text-decoration: none;
`;

const DropdownSubMenu = styled.ul<{ subMenuActive: boolean }>`
  position: absolute;
  background: ${({ theme }) => theme.colors.background};
  top: -0.25rem;
  left: 14rem;
  border: solid 1px inherit;
  width: 15rem;
  margin: 0;
  padding: 0;
  display: ${props => (props.subMenuActive ? 'block' : 'none')};
  box-shadow: rgba(0, 0, 0, 0.02) 0 2.8px 2.2px,
    rgba(0, 0, 0, 0.027) 0 6.7px 5.3px, rgba(0, 0, 0, 0.035) 0 12.5px 10px,
    rgba(0, 0, 0, 0.043) 0 22.3px 17.9px, rgba(0, 0, 0, 0.05) 0 41.8px 33.4px,
    rgba(0, 0, 0, 0.07) 0 100px 80px;
`;

interface DropdownMenuProps {
  dropdownActive: boolean;
}

const DropdownMenu = styled.div<DropdownMenuProps>`
  border: solid 1px inherit;
  display: ${props => (props.dropdownActive ? 'flex' : 'none')};
  background: ${({ theme }) => theme.colors.background};
  position: absolute;
  top: 122px;
  z-index: 2;
  box-shadow: rgba(0, 0, 0, 0.02) 0 2.8px 2.2px,
    rgba(0, 0, 0, 0.027) 0 6.7px 5.3px, rgba(0, 0, 0, 0.035) 0 12.5px 10px,
    rgba(0, 0, 0, 0.043) 0 22.3px 17.9px, rgba(0, 0, 0, 0.05) 0 41.8px 33.4px,
    rgba(0, 0, 0, 0.07) 0 100px 80px;
  color: inherit; // make prop?
  width: 15rem;

  ul {
    list-style: none;
    padding: 0.25rem 0;
    margin: 0;
    width: 100%;

    li {
      margin: 0;
      padding: 0;
      position: relative;
      z-index: 2;

      &:hover,
      &:focus {
        ${DropdownSubMenu} {
          display: block;
        }
      }

      a {
        font-size: 1rem;
        display: block;
        text-decoration: none;
        font-weight: inherit;
        padding: 0.5rem 1rem;
        transition: all 0.5s ease;
        color: inherit;

        &:hover {
          color: inherit;
          text-decoration: underline;
        }
      }
    }
  }

  @media (max-width: calc(${breakpoints.lg} - 1)) {
    display: none;
  }
`;

declare module 'styled-components' {
  export interface DefaultTheme {
    navigation: {
      DownArrow: React.FC<React.SVGProps<SVGSVGElement>>;
      downArrowSize: number;
      NavDesktopMenuItemFontSize: string;
      mainItemTextTransform: string;
      fontWeight: string;
      downArrowSpacing: string;
    };
    colors: {
      brandPrimary: Interpolation<
        ThemedStyledProps<
          ClassAttributes<HTMLDivElement> & HTMLAttributes<HTMLDivElement>,
          DefaultTheme
        >
      >;
      carouselBullet: Interpolation<
        ThemedStyledProps<
          ClassAttributes<HTMLDivElement> & HTMLAttributes<HTMLDivElement>,
          DefaultTheme
        >
      >;
      defaultFont: string;
      background: string;
    };
  }
}

const NavDesktopButton = styled.button`
  display: flex;
  align-items: center;
  background: inherit;
  align-self: stretch;
  font-weight: ${({ theme }) => theme.navigation.fontWeight};
  border: 0;
  color: inherit;
  padding: 0.5rem;
  text-decoration: none;
  text-transform: ${({ theme }) => theme.navigation.mainItemTextTransform};
  cursor: pointer;
  pointer-events: auto;
  svg {
    margin-left: ${({ theme }) => theme.navigation.downArrowSpacing};
  }

  a {
    color: inherit;
    font-weight: inherit;
  }
`;

interface SubSection {
  title: string;
  link?: {
    href?: string;
    blank?: boolean;
    internalLink?: any; // replace with appropriate type
  };
}

interface DropdownSubMenuItemsProps {
  items: SubSection[];
  subMenuActive: boolean;
  setSubMenuActive: (active: boolean) => void;
  setDropdownActive: (active: string) => void;
  getInternalLink: (link: any) => string;
}

interface LinkData {
  href?: string;
  linkType?: string;
  internalLink?: any; // Replace with proper type
}

interface DropdownMenuLinkItemProps {
  menuItem: {
    title: string;
    link?: LinkData;
    items?: any[];
  };
  isLastItem: boolean;
  subMenuActive: boolean;
  setSubMenuActive: (active: boolean) => void;
  setDropdownActive: (active: string) => void;
  getInternalLink: (link: any) => string;
}

interface Link {
  href?: string;
  linkType?: string;
  blank?: boolean;
  internalLink?: any; // Replace with actual type
}

interface MenuItem {
  title: string;
  link?: Link;
  items?: MenuItem[];
}

interface NavLink {
  title: string;
  menuType: string;
  items: MenuItem[];
}

interface DropdownMenuItemsProps {
  navLink: NavLink;
  dropdownActive: string;
  subMenuActive: boolean;
  setSubMenuActive: (active: boolean) => void;
  setDropdownActive: (active: string) => void;
  getInternalLink: (link: any) => string;
}

const DropdownSubMenuItems: React.FC<DropdownSubMenuItemsProps> = ({
  items,
  subMenuActive,
  setSubMenuActive,
  setDropdownActive,
  getInternalLink,
}) => {
  const shiftTabPressed = useRef(false);

  if (!items?.length) return null;

  return (
    <DropdownSubMenu subMenuActive={subMenuActive}>
      {items.map((subSection, index) => (
        <li key={subSection.title}>
          {subSection?.link?.href?.startsWith('http') ? (
            <a
              href={subSection.link.href}
              target={subSection.link.blank ? '_blank' : '_self'}
              rel="noreferrer"
            >
              {subSection.title}
            </a>
          ) : (
            <LinkComponent
              to={
                subSection.link?.internalLink
                  ? getInternalLink({
                      ...subSection.link,
                      reference: subSection.link.internalLink,
                    })
                  : subSection.link?.href
              }
              onKeyDown={event => {
                // Track if shift+tab was pressed
                if (event.key === 'Tab' && event.shiftKey) {
                  shiftTabPressed.current = true;
                } else {
                  shiftTabPressed.current = false;
                }
              }}
              onBlur={() => {
                if (index === items.length - 1 && !shiftTabPressed.current) {
                  setSubMenuActive(false);
                  setDropdownActive('');
                }
              }}
            >
              {subSection.title}
            </LinkComponent>
          )}
        </li>
      ))}
    </DropdownSubMenu>
  );
};

const DropdownMenuLinkItem: React.FC<DropdownMenuLinkItemProps> = ({
  menuItem,
  isLastItem,
  subMenuActive,
  setSubMenuActive,
  setDropdownActive,
  getInternalLink,
}) => {
  const shiftTabPressed = useRef(false);

  if (menuItem?.link?.linkType === 'external') return null;

  const linkHref = menuItem.link?.internalLink
    ? getInternalLink({
        ...menuItem.link,
        reference: menuItem.link.internalLink,
      })
    : menuItem?.link?.href;

  return (
    <LinkComponent
      to={linkHref}
      style={
        menuItem.items
          ? { display: 'flex', justifyContent: 'space-between' }
          : undefined
      }
      onFocus={() => {
        if (menuItem.items) setSubMenuActive(true);
      }}
      onKeyDown={event => {
        if (event.key === 'Enter') {
          setSubMenuActive(!subMenuActive);
        }

        // Track if shift+tab was pressed
        if (event.key === 'Tab' && event.shiftKey) {
          shiftTabPressed.current = true;
        } else {
          shiftTabPressed.current = false;
        }
      }}
      onBlur={() => {
        if (isLastItem && !shiftTabPressed.current) {
          setDropdownActive('');
          setSubMenuActive(false);
        }
      }}
    >
      {menuItem.title}
      {menuItem.items && (
        <ChevronRight height={22} width={22} style={{ marginTop: '.25rem' }} />
      )}
    </LinkComponent>
  );
};

const DropdownMenuItems: React.FC<DropdownMenuItemsProps> = ({
  navLink,
  dropdownActive,
  subMenuActive,
  setSubMenuActive,
  setDropdownActive,
  getInternalLink,
}) => {
  const shiftTabPressed = useRef(false);

  if (navLink.menuType !== 'dropdown') return null;

  return (
    <DropdownMenu dropdownActive={dropdownActive === navLink.title}>
      <ul>
        {navLink.items?.length &&
          navLink.items.map((menuItem, index) => {
            const isLastItem =
              index === navLink.items.length - 1 && !menuItem.items;

            return (
              <li key={menuItem.title}>
                {menuItem?.link?.linkType === 'external' && (
                  <LinkComponent
                    to={menuItem.link?.href}
                    target={menuItem?.link?.blank ? `_blank` : `_self`}
                    style={
                      menuItem.items
                        ? { display: 'flex', justifyContent: 'space-between' }
                        : undefined
                    }
                    onFocus={() => {
                      if (menuItem.items) setSubMenuActive(true);
                    }}
                    onKeyDown={event => {
                      if (event.key === 'Enter') {
                        setSubMenuActive(!subMenuActive);
                      }

                      // Track if shift+tab was pressed
                      if (event.key === 'Tab' && event.shiftKey) {
                        shiftTabPressed.current = true;
                      } else {
                        shiftTabPressed.current = false;
                      }
                    }}
                    onBlur={() => {
                      if (isLastItem && !shiftTabPressed.current) {
                        setDropdownActive('');
                        setSubMenuActive(false);
                      }
                    }}
                  >
                    {menuItem.title}
                    {menuItem.items && (
                      <ChevronRight
                        height={22}
                        width={22}
                        style={{ marginTop: '.25rem' }}
                      />
                    )}
                  </LinkComponent>
                )}

                <DropdownMenuLinkItem
                  menuItem={menuItem}
                  isLastItem={isLastItem}
                  subMenuActive={subMenuActive}
                  setSubMenuActive={setSubMenuActive}
                  setDropdownActive={setDropdownActive}
                  getInternalLink={getInternalLink}
                />

                {menuItem.items && (
                  <DropdownSubMenuItems
                    items={menuItem.items}
                    subMenuActive={subMenuActive}
                    setSubMenuActive={setSubMenuActive}
                    setDropdownActive={setDropdownActive}
                    getInternalLink={getInternalLink}
                  />
                )}
              </li>
            );
          })}
      </ul>
    </DropdownMenu>
  );
};

function NavDesktop({
  navLinks = [],
  megaMenuActive = null,
  setKeyboardNavActive = () => {},
  setMegaMenuActive = () => {},
  keyboardNavActive = false,
  getInternalLink = () => '',
}: Readonly<NavDesktopProps>) {
  const theme = useTheme();
  const shopNavItem = React.useRef(null);
  const [dropdownActive, setDropdownActive] = useState(null);
  const [subMenuActive, setSubMenuActive] = useState(false);

  useEffect(() => {
    if (megaMenuActive < 0 && keyboardNavActive) {
      shopNavItem.current.focus();
    }
  }, [keyboardNavActive, megaMenuActive]);

  return (
    <Wrapper>
      {navLinks.map((navLink, megaMenuIndex) => (
        <NavDesktopMenuItem
          key={navLink.title}
          onMouseEnter={() => {
            if (navLink.menuType === 'megaMenu')
              setMegaMenuActive(megaMenuIndex);
            if (navLink.menuType === 'dropdown')
              setDropdownActive(navLink.title);
          }}
          onMouseLeave={() => {
            if (navLink.menuType === 'megaMenu') setMegaMenuActive(null);
            if (navLink.menuType === 'dropdown') setDropdownActive(null);
          }}
          onClick={() => {
            if (navLink.menuType === 'megaMenu') {
              setMegaMenuActive(
                megaMenuActive === megaMenuIndex ? null : megaMenuIndex
              );
            }
            if (navLink.menuType === 'dropdown') {
              if (dropdownActive) {
                setDropdownActive(null);
              } else {
                setDropdownActive(navLink.title);
              }
            }
          }}
          onKeyDown={event => {
            if (event.key === 'Enter') {
              setKeyboardNavActive(true);
            }
            if (event.key === 'Escape') {
              if (navLink.menuType === 'megaMenu') setMegaMenuActive(null);
              if (dropdownActive) {
                setDropdownActive(null);
              } else {
                setDropdownActive(navLink.title);
              }
            }
          }}
        >
          {navLink.menuType === 'megaMenu' && (
            <NavDesktopButton
              id={`menu-trigger-${megaMenuIndex}`}
              ref={shopNavItem}
              aria-expanded={
                megaMenuActive === megaMenuIndex ||
                dropdownActive === navLink.title
              }
            >
              {navLink.title}
              <theme.navigation.DownArrow
                width={theme.navigation.downArrowSize}
                height={theme.navigation.downArrowSize}
              />
            </NavDesktopButton>
          )}
          {navLink.menuType === 'dropdown' && (
            <NavDesktopButton
              id={`menu-trigger-${megaMenuIndex}`}
              onFocus={() => {
                setMegaMenuActive(null);
                // if dropdown is active, close it if trigger is refocused
                if (dropdownActive === navLink.title) {
                  setDropdownActive(null);
                }
              }}
              aria-expanded={
                megaMenuActive === megaMenuIndex ||
                dropdownActive === navLink.title
              }
            >
              {navLink.title}
              <theme.navigation.DownArrow
                width={theme.navigation.downArrowSize}
                height={theme.navigation.downArrowSize}
              />
            </NavDesktopButton>
          )}
          <DropdownMenuItems
            navLink={navLink}
            dropdownActive={dropdownActive}
            subMenuActive={subMenuActive}
            setSubMenuActive={setSubMenuActive}
            setDropdownActive={setDropdownActive}
            getInternalLink={getInternalLink}
          />
          {navLink.menuType === 'static' && (
            <NavDesktopLink
              href={navLink.link?.href || '#'}
              id={`menu-trigger-${megaMenuIndex}`}
            >
              {navLink.title}
            </NavDesktopLink>
          )}
        </NavDesktopMenuItem>
      ))}
    </Wrapper>
  );
}

export default NavDesktop;
